import { useState } from "react";
import { Sparkles, Loader2, RefreshCw, Check, AlertCircle, Target } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * SessionImpactCard — Iter S
 * Generates + displays the AI Session Impact Summary for the current session.
 * Persists on outcome.impact_summary so reopening a session shows the same summary.
 *
 * Props:
 *   sessionId    — required
 *   impact       — current outcome.impact_summary (or null)
 *   onUpdate(s)  — called with the new summary object after a generate/regenerate
 */
export default function SessionImpactCard({ sessionId, impact, onUpdate }) {
  const [busy, setBusy] = useState(false);

  const generate = async (force = false) => {
    if (!sessionId) {
      toast.error("Save the session first to generate an impact summary.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/impact-summary`, { force_regenerate: force });
      onUpdate?.(r.data);
      toast.success(force ? "Impact summary regenerated." : "Impact summary ready.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(e.response?.data?.detail || "You've hit your AI quota.");
      } else if (status === 503) {
        toast.error("AI temporarily unavailable. Try again shortly.");
      } else {
        toast.error("Couldn't generate impact summary.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!impact) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="impact-summary-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="label-overline text-sage-700 mb-1.5 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI session impact summary
            </div>
            <h3 className="font-heading text-xl sm:text-2xl text-ink-900 mb-1">What did today actually change?</h3>
            <p className="text-sm text-ink-600 leading-relaxed max-w-xl">
              One AI-generated bottom-line your client can take home — pulled from pain, ROM, captures, SOAP and home care.
            </p>
          </div>
          <button
            onClick={() => generate(false)}
            disabled={busy}
            data-testid="generate-impact-btn"
            className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Generating…" : "Generate impact summary"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 space-y-5" data-testid="impact-summary-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="label-overline text-sage-700 mb-1.5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> AI session impact summary
          </div>
          {impact.headline && (
            <h3 className="font-heading text-2xl sm:text-3xl font-light text-ink-900 leading-tight" data-testid="impact-headline">
              {impact.headline}
            </h3>
          )}
        </div>
        <button
          onClick={() => generate(true)}
          disabled={busy}
          data-testid="regenerate-impact-btn"
          className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-50 text-ink-700 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Regenerate
        </button>
      </div>

      {(impact.wins?.length || 0) > 0 && (
        <div>
          <div className="label-overline text-sage-700 mb-2 inline-flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" /> Wins
          </div>
          <ul className="space-y-1.5" data-testid="impact-wins">
            {impact.wins.map((w, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-ink-700 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage-500 flex-none" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(impact.concerns?.length || 0) > 0 && (
        <div>
          <div className="label-overline text-amber-600 mb-2 inline-flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> Keep an eye on
          </div>
          <ul className="space-y-1.5" data-testid="impact-concerns">
            {impact.concerns.map((c, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-ink-700 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-none" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {impact.next_session_focus && (
        <div className="bg-paper-50 border border-paper-200 rounded-2xl p-4" data-testid="impact-next-focus">
          <div className="label-overline text-ink-500 mb-1.5 inline-flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" /> Next session focus
          </div>
          <p className="text-sm text-ink-700 leading-relaxed">{impact.next_session_focus}</p>
        </div>
      )}

      {impact.body_paragraph && (
        <div>
          <div className="label-overline text-ink-500 mb-2">For the progress report</div>
          <p className="text-sm text-ink-700 leading-relaxed italic" data-testid="impact-body-paragraph">
            &ldquo;{impact.body_paragraph}&rdquo;
          </p>
        </div>
      )}
    </div>
  );
}
